import {create} from 'zustand';
import {Series} from "../models/interfaces/series.interface.ts";
import {SeriesDto} from "../models/interfaces/dtos/series.dto.interface.ts";
import {fetchSeries, fetchUserSeries, updateSeriesInfo} from "../services/seriesService.ts";
import {transformSingleSeriesDtoToSingleSeries} from "../utils/transformers/series.transformer.ts";
import {UserBulkSeriesDto} from "../models/interfaces/dtos/userBulkSeries.dto.interface.ts";
import useUserStore from "./useUserStore.ts";

interface SeriesStore {
  seriesNameIdMapping: Record<string, string>;
  seriesNameIdMappingStatus: 'idle' | 'loading' | 'loaded' | 'error';
  resetSeriesNameIdMapping: () => void;
  series: Record<string, Series>;
  getSeriesNameIdMapping: () => Promise<void>;
  getSeries: (seriesId: string) => Promise<void>;
  updateSeries: (series: Series) => Promise<void>;

}

let mappingRequest: Promise<void> | null = null;

export const useSeriesStore = create<SeriesStore>((set) => ({
  seriesNameIdMapping: {},
  seriesNameIdMappingStatus: 'idle',
  resetSeriesNameIdMapping: () => {
    mappingRequest = null;
    set({seriesNameIdMapping: {}, seriesNameIdMappingStatus: 'idle'});
  },
  series: {} as Record<string, Series>,
  getSeriesNameIdMapping: (): Promise<void> => {
    if (mappingRequest) return mappingRequest;

    const request = fetchUserSeries().then((userBulkSeriesDto: UserBulkSeriesDto[]) => {
      const seriesNameIdMapping: Record<string, string> = {};
      userBulkSeriesDto.forEach((seriesDto: UserBulkSeriesDto) => {
        seriesNameIdMapping[seriesDto.name] = seriesDto._id;
      });
      // A reset or session change makes an older response irrelevant.
      if (mappingRequest === request) {
        set({seriesNameIdMapping, seriesNameIdMappingStatus: 'loaded'});
      }
    }).catch((error) => {
      if (mappingRequest === request) set({seriesNameIdMappingStatus: 'error'});
      console.error('Error fetching series:', error);
      throw error;
    }).finally(() => {
      if (mappingRequest === request) mappingRequest = null;
    });
    mappingRequest = request;
    set({seriesNameIdMappingStatus: 'loading'});
    return request;
  },
  getSeries: async (seriesId: string): Promise<void> => {
    try {
      const seriesDto: SeriesDto = await fetchSeries(seriesId)
      const series: Series = transformSingleSeriesDtoToSingleSeries(seriesDto)
      set((state: SeriesStore) => ({
          series: {
            ...state.series,
            [seriesId]: series,
          },
        }
      ));
    } catch (error) {
      console.error('Error fetching series:', error);
      throw error;
    }
  },
  updateSeries: async (series: Series) => {
    try {
      const updatedSeriesDto: SeriesDto = await updateSeriesInfo(series.id, series.info);
      const updatedSeries: Series = transformSingleSeriesDtoToSingleSeries(updatedSeriesDto);

      set((state: SeriesStore) => ({
        series: {
          ...state.series,
          [series.id]: updatedSeries
        },
      }));
    } catch (error) {
      console.error('Error updating series:', error);
      throw error;
    }
  }
}));

useUserStore.subscribe((state, previous) => {
  if (state.user?._id !== previous.user?._id || state.user?.workspaceId !== previous.user?.workspaceId) {
    useSeriesStore.getState().resetSeriesNameIdMapping();
  }
});